import { ArrowLeft, Calendar, Heart, Info } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useState, useEffect } from "react";
import { get } from "@/services/axiosUitils";
import welcomeBg from "../../assets/homebg.png";

const HRVDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("weekly");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hrvData, setHrvData] = useState<any[]>([]);

  // Get selected date from FitnessPage navigation
  const selectedDate = location.state?.selectedDate || null;
  const selectedDateObj = location.state?.selectedDateObj || null;

  const [summaryData, setSummaryData] = useState({
    hrv: "0",
    average_hrv: 0,
    days: 7,
    weeks: 4,
  });
  const [dateRange, setDateRange] = useState({
    start_date: "",
    end_date: "",
  });

  // Fetch HRV data from API
  const fetchHRVData = async (type: "weekly" | "monthly") => {
    try {
      setLoading(true);
      setError(null);

      // Build API URL with date parameter if available
      const dateParam = selectedDate ? `&date=${selectedDate}` : "";
      const apiUrl = `fitbit_scan/hrv-graph/?type=${type}${dateParam}`;

      const response = await get(apiUrl, {
        headers: {
          "X-Tenant-Id": "ab9bb443-b2ef-4b55-b0ed-0b4a41bb2dc8",
        },
      });

      // Handle both boolean and object success formats
      const isSuccess =
        typeof response.success === "boolean"
          ? response.success
          : response.success?.code === "Retrieved";

      if (isSuccess && response.summary) {
        setSummaryData(response.summary);
        setDateRange({
          start_date: response.start_date,
          end_date: response.end_date,
        });

        // Transform API data to match chart format
        if (type === "weekly") {
          const transformedData = response.graph.map((item: any) => ({
            day: item.day,
            hrv: item.value,
          }));
          setHrvData(transformedData);
        } else {
          const transformedData = response.graph.map((item: any) => ({
            week: item.week,
            hrv: item.value,
          }));
          setHrvData(transformedData);
        }
      } else {
        setError("Failed to fetch HRV data");
        setHrvData([]);
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch HRV data");
      setHrvData([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (tab: "weekly" | "monthly") => {
    setActiveTab(tab);
    fetchHRVData(tab);
  };

  // Load initial data
  useEffect(() => {
    fetchHRVData("weekly");
  }, []);

  // Format date range for display
  const formatDateRange = () => {
    if (!dateRange.start_date || !dateRange.end_date) {
      return "";
    }
    const startDate = new Date(dateRange.start_date).toLocaleDateString(
      "en-US",
      {
        month: "short",
        day: "numeric",
      }
    );
    const endDate = new Date(dateRange.end_date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return `${startDate} - ${endDate}`;
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              navigate("/glucera/fitness", {
                state: {
                  selectedDate: selectedDate,
                  selectedDateObj: location.state?.selectedDateObj,
                },
              })
            }
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 text-center">
            <h1 className="text-xl font-bold text-foreground">
              Heart Rate Variability
            </h1>
            <p className="text-xs text-muted-foreground">
              {selectedDate ? selectedDate : "Your HRV insights"}
            </p>
          </div>
          <div className="w-10" />
        </div>

        {/* Sleep Tracking Required Alert */}
        <Card className="rounded-2xl border border-orange-200 bg-orange-50 p-4 dark:border-orange-800 dark:bg-orange-950">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900">
              <Info className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="flex-1">
              <h3 className="mb-1 text-sm font-semibold text-orange-900 dark:text-orange-100">
                Sleep Tracking Required
              </h3>
              <p className="text-xs text-orange-700 dark:text-orange-300">
                HRV is recorded only when you wear your tracker watch while
                sleeping. Make sure to wear it overnight for accurate readings.
              </p>
            </div>
          </div>
        </Card>

        {/* Current HRV Card */}
        <Card className="rounded-2xl border border-border bg-gradient-to-br from-pink-50 to-rose-100 p-5 shadow-lg dark:from-pink-950 dark:to-rose-900">
          <div className="mb-4 flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-pink-500/20">
              <Heart className="h-7 w-7 text-pink-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">Current HRV</p>
              <div className="flex items-baseline gap-2">
                <div className="text-4xl font-bold text-foreground">
                  {loading
                    ? "..."
                    : summaryData.hrv === "0" || summaryData.hrv === "0.0"
                      ? "--"
                      : parseFloat(summaryData.hrv).toFixed(1)}
                </div>
                <span className="text-sm text-muted-foreground">ms</span>
              </div>
              {formatDateRange() && (
                <p className="text-xs text-gray-500">{formatDateRange()}</p>
              )}
            </div>
          </div>
        </Card>

        {/* Chart Card */}
        <Card className="rounded-2xl border border-border bg-pink-50/30 p-4 shadow-lg dark:bg-pink-950/30">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-semibold text-foreground">
              {activeTab === "weekly" ? "Weekly" : "Monthly"} Trend
            </h3>
            <div className="flex gap-2 rounded-lg bg-background/50 p-1">
              <Button
                variant={activeTab === "weekly" ? "default" : "ghost"}
                size="sm"
                onClick={() => handleTabChange("weekly")}
                className="h-7 text-xs"
              >
                Weekly
              </Button>
              <Button
                variant={activeTab === "monthly" ? "default" : "ghost"}
                size="sm"
                onClick={() => handleTabChange("monthly")}
                className="h-7 text-xs"
              >
                Monthly
              </Button>
            </div>
          </div>

          <div className="h-64">
            {loading ? (
              <div className="flex h-full items-center justify-center">
                <div className="text-sm text-muted-foreground">Loading...</div>
              </div>
            ) : hrvData.length === 0 ? (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    No data available
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Wear your tracker while sleeping to record HRV
                  </p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={hrvData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis
                    dataKey={activeTab === "weekly" ? "day" : "week"}
                    tick={{ fontSize: 12 }}
                    stroke="currentColor"
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    stroke="currentColor"
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "2px solid hsl(var(--border))",
                      borderRadius: "12px",
                      color: "hsl(var(--foreground))",
                      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.2)",
                    }}
                    itemStyle={{ color: "hsl(var(--foreground))" }}
                    labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                    formatter={(value: any) => [`${value} ms`, "HRV"]}
                  />
                  <Line
                    type="monotone"
                    dataKey="hrv"
                    stroke="#ec4899"
                    strokeWidth={2}
                    dot={{ fill: "#ec4899", r: 4 }}
                    name="HRV"
                    strokeDasharray={
                      hrvData.every(d => d.hrv === 0) ? "5 5" : "0"
                    }
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* What is HRV Section */}
        <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="font-semibold text-foreground">What is HRV?</h3>
          </div>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong className="text-foreground">
                Heart Rate Variability (HRV)
              </strong>{" "}
              measures the variation in time between each heartbeat. It's
              controlled by your autonomic nervous system and reflects your
              body's ability to adapt to stress.
            </p>
            <p>
              A <strong className="text-foreground">higher HRV</strong>{" "}
              generally indicates better cardiovascular fitness, stress
              resilience, and recovery capacity. It suggests your body can
              efficiently switch between "fight or flight" and "rest and digest"
              modes.
            </p>
            <p>
              A <strong className="text-foreground">lower HRV</strong> may
              indicate stress, fatigue, overtraining, or illness. Factors like
              sleep quality, hydration, alcohol consumption, and exercise
              intensity can affect your HRV.
            </p>
          </div>
        </Card>

        {/* How to Improve HRV */}
        <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
          <h3 className="mb-3 font-semibold text-foreground">
            How to Improve HRV
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-pink-500" />
              <span>Get consistent, quality sleep (7-9 hours)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-pink-500" />
              <span>Practice deep breathing and meditation</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-pink-500" />
              <span>Stay hydrated throughout the day</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-pink-500" />
              <span>Avoid alcohol before bed</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-pink-500" />
              <span>Balance intense workouts with recovery days</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default HRVDetail;
